import { Component, OnInit, OnDestroy, HostListener } from '@angular/core';

import { FeedbackCardService } from 'app/helpers/services/feedback-card/feedback-card.service';
import { CompRouterService } from 'app/helpers/services/core/comp-router.service';
import { UsersService } from 'app/helpers/services/user-directory/users.service';
import { UtilsService } from 'app/helpers/services/core/utils.service';
import { ConfirmationService } from 'primeng/primeng';
import * as moment from 'moment';

@Component({
  selector: 'app-moc-detailed-analytics',
  templateUrl: './moc-detailed-analytics.component.html',
  styleUrls: ['./moc-detailed-analytics.component.scss'],
  providers: [ConfirmationService]
})
export class MocDetailedAnalyticsComponent implements OnInit, OnDestroy {


  analyticsData: any;
  loading = false;
  dateRangeText: string = '';
  daterange: any = {};
  errorMessage: string | null = null;

  aiSummary: string = '';
  aiSummaryLoading: boolean = false;
  aiSummaryError: boolean = false;
  aiSummaryEmpty: boolean = false;

  // Tab Navigation
  activeTab: number = 1;

  // Tab 5: Controls — Section A
  controlsStartDate: Date = null;
  controlsEndDate: Date = null;
  controlsDateRange: Date[] = [];
  controlsDownloading: boolean = false;

  // Tab 5: Controls — Section B (UI only, no API)
  goodOptions: { id: number; label: string; active: boolean; displayOrder: number; allowsOtherText: boolean }[] = [];
  badOptions: { id: number; label: string; active: boolean; displayOrder: number; allowsOtherText: boolean }[] = [];
  goodPanelTitle: string = '';
  badPanelTitle: string = '';
  goodQuestionId: number | null = null;
  badQuestionId: number | null = null;
  feedbackOptionsLoading: boolean = false;
  feedbackOptionsError: boolean = false;
  newGoodOption: string = '';
  newBadOption: string = '';
  addingGoodOption: boolean = false;
  addingBadOption: boolean = false;

  weekImpacts: { id: string; text: string; date: string }[] = [];
  impactsLoading: boolean = false;
  newImpact: string = '';
  addingImpact: boolean = false;
  expandedImpactIds: Set<string> = new Set();
  readonly impactMaxLength = 240;

  // Tab 2: Detailed Responses
  responsesData: any[] = [];
  totalResponsesCount: number = 0;
  responsesLoading = false;
  currentPage: number = 1;
  pageSize: number = 200;
  totalPages: number = 0;
  selectedRating: number | null = null;

  // Tab 2: Filters
  mocFilterLoaded = false;
  activeFilters: any = {};
  private lastFilterJson = '';
  private skipNextFilterEmit = false;
  mocResource = { type: 'mocResponses' };
  mocFilters: any[] = [
    {
      name: 'Rating', type: 'rating', nameField: 'name', idField: 'id',
      list: [
        { id: 5, name: '5 - Excellent' },
        { id: 4, name: '4 - Good' },
        { id: 3, name: '3 - Okay' },
        { id: 2, name: '2 - Not so good' },
        { id: 1, name: '1 - Bad' },
      ]
    },
    {
      name: 'Visibility', type: 'anonymity', nameField: 'name', idField: 'id',
      list: [
        { id: 'anonymous', name: 'Anonymous' },
        { id: 'named', name: 'Named' },
      ]
    },
    {
      name: 'Response Detail', type: 'responseType', nameField: 'name', idField: 'id',
      list: [
        { id: 'hasGood', name: 'Positive Feedback' },
        { id: 'hasBad', name: 'Negative Feedback' },
        { id: 'hasGoodOthers', name: 'Positive Remark' },
        { id: 'hasBadOthers', name: 'Negative Remark' },
        { id: 'noFeedback', name: 'No Feedback' },
      ]
    },
    {
      name: 'IBU / Department', type: 'ibu', nameField: 'name', idField: 'id',
      list: []
    },
    {
      name: 'Worksite', type: 'workSite', nameField: 'name', idField: 'id',
      list: []
    },
    {
      name: 'Tenure', type: 'tenure', nameField: 'name', idField: 'id',
      list: [
        { id: '0-1', name: '0–1 year' },
        { id: '1-3', name: '1–3 years' },
        { id: '3-5', name: '3–5 years' },
        { id: '5-10', name: '5–10 years' },
        { id: '10+', name: '10+ years' },
      ]
    },
    {
      name: 'Experience', type: 'experience', nameField: 'name', idField: 'id',
      list: [
        { id: '0-1', name: '0–1 year' },
        { id: '1-3', name: '1–3 years' },
        { id: '3-5', name: '3–5 years' },
        { id: '5-10', name: '5–10 years' },
        { id: '10+', name: '10+ years' },
      ]
    },
  ];

  // Tab 3: Category Analytics
  categoryAnalyticsData: any = null;
  goodCategoryChart: any = null;
  badCategoryChart: any = null;

  // Tab 4: Trend Analytics
  trendData: any = null;
  goodTrendCharts: any[] = [];

  // Overview: KPI Cards
  dashboardStats: any = null;

  // Analytics Tab: Project Rankings
  topProjects: any[] = [];
  bottomProjects: any[] = [];

  // Trends Tab: computed SVG path strings from API
  ratingChartLine: string = '';
  ratingChartPolygon: string = '';
  ratingYAxisLabels: any[] = [];
  sentimentPosLine: string = '';
  sentimentNegLine: string = '';
  volumeBarData: { count: number; height: number }[] = [];

  private buildWeeklyCharts(weeks: any[]) {
    const xStep = 80;
    const xStart = 40;

    // Rating over time: viewBox 0 0 800 160, dynamic scale with padding so variations are visible
    const ratings = weeks.map(w => w.avgRating);
    const minR = Math.min(...ratings);
    const maxR = Math.max(...ratings);
    const pad = 0.15;
    const scaleMin = Math.max(1, Math.floor((minR - pad) * 10) / 10);
    const scaleMax = Math.min(5, Math.ceil((maxR + pad) * 10) / 10);
    const ratingToY = (r: number) => Math.round(20 + 120 - ((r - scaleMin) / (scaleMax - scaleMin)) * 120);

    this.ratingOverTimeData = weeks.map((w, i) => ({
      rating: w.avgRating,
      week: w.weekStart ? moment(w.weekStart).format('DD MMM') : w.week,
      cx: xStart + i * xStep,
      cy: ratingToY(w.avgRating)
    }));
    const lastX = xStart + (weeks.length - 1) * xStep;
    this.ratingChartLine = this.ratingOverTimeData.map(d => `${d.cx},${d.cy}`).join(' ');
    this.ratingChartPolygon = this.ratingChartLine + ` ${lastX},160 ${xStart},160`;

    // Y-axis ticks: 0.5 steps within the visible scale range
    const tickStep = (scaleMax - scaleMin) <= 2 ? 0.5 : 1;
    const ticks: number[] = [];
    for (let v = Math.ceil(scaleMin / tickStep) * tickStep; v <= scaleMax + 0.01; v = Math.round((v + tickStep) * 10) / 10) {
      ticks.push(v);
    }
    this.ratingYAxisLabels = ticks.map(v => ({
      value: v % 1 === 0 ? v : v.toFixed(1),
      topPct: (ratingToY(v) / 160 * 100).toFixed(1)
    }));

    // Sentiment chart: viewBox 0 0 800 140, fixed scale 1–5
    const sentToY = (r: number) => Math.round(15 + 110 - ((r - 1) / 4) * 110);

    this.sentimentTrendData = weeks.map((w, i) => ({
      week: w.weekStart ? moment(w.weekStart).format('DD MMM') : w.week,
      cx: xStart + i * xStep,
      posY: sentToY(w.positiveAvgRating),
      negY: sentToY(w.negativeAvgRating),
      posChip: w.positiveChip,
      posRating: w.positiveAvgRating,
      posCount: w.positiveChipCount,
      negChip: w.negativeChip,
      negRating: w.negativeAvgRating,
      negCount: w.negativeChipCount
    }));
    this.sentimentPosLine = this.sentimentTrendData.map(d => `${d.cx},${d.posY}`).join(' ');
    this.sentimentNegLine = this.sentimentTrendData.map(d => `${d.cx},${d.negY}`).join(' ');

    // Volume bars: max bar height 52px
    const maxCount = Math.max(...weeks.map(w => w.responseCount));
    this.volumeBarData = weeks.map(w => ({
      count: w.responseCount,
      height: Math.max(4, Math.round((w.responseCount / maxCount) * 52))
    }));
  }

  fetchWeeklyTrends() {
    const req = {
      formId: '1682358502664',
      weekCount: 10
    };
    this.feedbackService.getMocWeeklyTrends(req).subscribe((response: any) => {
      if (response && response.statusCode === 200) {
        this.buildWeeklyCharts(response.responseData.weeks);
      }
    }, error => {
      console.error('Error fetching weekly trends:', error);
    });
  }

  // Trends Tab: Most positive vs most negative feedback
  sentimentTrendData: any[] = [];
  activeSentimentDotWeek: number = -1;
  sentimentTooltipVisible: boolean = false;
  sentimentTooltipData: any = null;
  sentimentTooltipX: number = 0;
  sentimentTooltipY: number = 0;

  get maxSentimentCount(): number {
    if (!this.sentimentTrendData.length) return 1;
    return Math.max(1, ...this.sentimentTrendData.map(d => Math.max(d.posCount || 0, d.negCount || 0)));
  }

  showSentimentTooltip(event: MouseEvent, d: any, i: number) {
    this.activeSentimentDotWeek = i;
    this.sentimentTooltipData = d;
    const target = event.currentTarget as HTMLElement;
    const container = target.parentElement.parentElement as HTMLElement;
    const containerRect = container.getBoundingClientRect();
    const targetRect = target.getBoundingClientRect();
    this.sentimentTooltipX = targetRect.left - containerRect.left + targetRect.width / 2;
    this.sentimentTooltipY = targetRect.top - containerRect.top - 10;
    this.sentimentTooltipVisible = true;
  }

  hideSentimentTooltip() {
    this.sentimentTooltipVisible = false;
    this.activeSentimentDotWeek = -1;
    this.sentimentTooltipData = null;
  }

  // Trends Tab: Average rating over time
  ratingOverTimeData: any[] = [];
  activeRatingDotIndex: number = -1;
  ratingTooltipVisible: boolean = false;
  ratingTooltipContent: string = '';
  ratingTooltipX: number = 0;
  ratingTooltipY: number = 0;

  showRatingTooltip(event: MouseEvent, d: any, i: number) {
    this.ratingTooltipContent = d.rating.toFixed(1);
    this.activeRatingDotIndex = i;
    const target = event.currentTarget as HTMLElement;
    const container = target.parentElement.parentElement as HTMLElement;
    const containerRect = container.getBoundingClientRect();
    const targetRect = target.getBoundingClientRect();
    this.ratingTooltipX = targetRect.left - containerRect.left + targetRect.width / 2;
    this.ratingTooltipY = targetRect.top - containerRect.top - 10;
    this.ratingTooltipVisible = true;
  }

  hideRatingTooltip() {
    this.ratingTooltipVisible = false;
    this.activeRatingDotIndex = -1;
  }

  // Overview: Feedback Sentiment
  activeTooltipIndex: number = -1;
  sentimentData: any[] = [];

  getProjectRatingColor(rating: number): string {
    if (rating >= 4.0) return '#43A047';
    if (rating >= 3.0) return '#FFC200';
    return '#E8372D';
  }

  getSentimentColor(goodPct: number): string {
    if (goodPct >= 60) return '#43A047';
    if (goodPct >= 45) return '#FFC200';
    return '#E8372D';
  }
  badTrendCharts: any[] = [];

  constructor(
    private feedbackService: FeedbackCardService,
    public compRouter: CompRouterService,
    private usersService: UsersService,
    private utils: UtilsService,
    private confirmationService: ConfirmationService
  ) {}

  ngOnInit() {
    this.initializeDefaultDateRange();
    this.loadIbuList();
    this.loadSiteList();
    this.mocFilterLoaded = true;
    this.loadActiveTabData();
  }

  ngOnDestroy() { }

  onWeekSelected(event: { start: moment.Moment; end: moment.Moment }) {
    this.daterange.start = event.start;
    this.daterange.end = event.end;
    this.dateRangeText = `${event.start.format('MMM D, YYYY')} - ${event.end.format('MMM D, YYYY')}`;
    this.loadActiveTabData();
  }

  fetchAiSummary() {
    this.aiSummaryLoading = true;
    this.aiSummary = '';
    this.aiSummaryError = false;
    this.aiSummaryEmpty = false;
    const weekStartDate = this.daterange.start.format('YYYY-MM-DD');
    this.feedbackService.getMocAiSummary(weekStartDate).subscribe(
      (response: any) => {
        if (response && response.response_data && response.response_data.summary_text) {
          this.aiSummary = response.response_data.summary_text;
        } else {
          this.aiSummaryEmpty = true;
        }
        this.aiSummaryLoading = false;
      },
      (error: any) => {
        this.aiSummaryLoading = false;
        const isNotFound = (typeof error === 'string' && error.toLowerCase() === 'not found') || (error && error.status === 404);
        if (isNotFound) {
          this.aiSummaryEmpty = true;
        } else {
          this.aiSummaryError = true;
        }
      }
    );
  }

  private loadIbuList() {
    this.usersService.getAllBusinessUnit().subscribe((response: any) => {
      if (response && response.responseData && response.responseData.businessUnits) {
        const ibuFilter = this.mocFilters.find(f => f.type === 'ibu');
        if (ibuFilter) {
          ibuFilter.list = response.responseData.businessUnits.map((bu: any) => ({
            id: bu.id,
            name: bu.buName
          }));
        }
      }
    });
  }

  private loadSiteList() {
    this.usersService.getAllSites().subscribe((response: any) => {
      if (response && response.responseData) {
        const siteFilter = this.mocFilters.find(f => f.type === 'workSite');
        if (siteFilter) {
          siteFilter.list = response.responseData.map((site: any) => ({
            id: site.id,
            name: site.name
          }));
        }
      }
    });
  }

  onTabChange(tabIndex: number) {
    this.activeTab = tabIndex;
    this.loadActiveTabData();
  }

  private loadActiveTabData() {
    switch (this.activeTab) {
      case 1:
        this.fetchDashboardStats();
        this.fetchCategoryAnalytics();
        this.loadWeekImpacts(this.daterange.start.format('YYYY-MM-DD'));
        break;
      case 2:
        this.fetchDashboardStats();
        this.fetchDetailedResponses();
        this.fetchAiSummary();
        break;
      case 3:
        this.fetchCategoryAnalytics();
        this.fetchAnalytics();
        this.fetchProjectAnalytics();
        break;
      case 4:
        this.fetchCategoryTrend();
        this.fetchWeeklyTrends();
        break;
      case 5:
        this.loadWeekImpacts(this.getCurrentWeekStartDate());
        this.loadFeedbackOptions();
        break;
    }
  }

  private getWeekBounds(anyDay: moment.Moment): { start: moment.Moment; end: moment.Moment } {
    // Week runs Friday–Thursday. dayOfWeek: 0=Sun,1=Mon,2=Tue,3=Wed,4=Thu,5=Fri,6=Sat
    const daysSinceFriday = (anyDay.day() + 2) % 7;
    const start = anyDay.clone().subtract(daysSinceFriday, 'days').startOf('day');
    const end = start.clone().add(6, 'days').endOf('day');
    return { start, end };
  }

  private initializeDefaultDateRange(): void {
    const { start, end } = this.getWeekBounds(moment());
    this.daterange = { start, end };
    this.dateRangeText = `${start.format('MMM D, YYYY')} - ${end.format('MMM D, YYYY')}`;
  }

  private sortRanges(ranges: any): { key: string, value: number }[] {
    if (!ranges) return [];

    const order = ['0-1', '1-3', '3-5', '5-10', '10+'];
    const sorted: { key: string, value: number }[] = [];

    order.forEach(key => {
      if (ranges.hasOwnProperty(key)) {
        sorted.push({
          key: key,
          value: Number(ranges[key])
        });
      }
    });

    return sorted;
  }

  fetchAnalytics() {
    this.loading = true;
    this.errorMessage = null;

    if (!this.daterange.start || !this.daterange.end) return;

    const startDate = moment(this.daterange.start).startOf('day').valueOf();
    const endDate = moment(this.daterange.end).endOf('day').valueOf();

    const req = {
      formId: '1682358502664',
      startDate: startDate,
      endDate: endDate
    };

    this.feedbackService.getMoodOfTheCrewDetailedAnalytics(req).subscribe((response: any) => {
      this.loading = false;
      if (response && response.statusCode === 200) {
        this.analyticsData = {
          ...response.responseData,
          tenureAnalytics: this.sortRanges(response.responseData.tenureAnalytics),
          experienceAnalytics: this.sortRanges(response.responseData.experienceAnalytics)
        };
      } else {
        this.analyticsData = null;
        this.errorMessage = 'The analytics service is temporarily unavailable. Please try again later.';
      }
    },
      (error) => {
        this.loading = false;
        this.analyticsData = null;
        this.errorMessage = 'The analytics service is temporarily unavailable. Please try again later.';
        console.error('Error fetching analytics data:', error);
      });
  }

  fetchDetailedResponses(page: number = 1) {
    this.currentPage = page;
    this.loading = true;
    this.errorMessage = null;
    this.skipNextFilterEmit = true;
    const req: any = {
      formId: '1682358502664',
      startDate: moment(this.daterange.start).startOf('day').valueOf(),
      endDate: moment(this.daterange.end).endOf('day').valueOf(),
      page: this.currentPage,
      size: this.pageSize
    };

    const filters: any = {};

    if (this.activeFilters.rating && this.activeFilters.rating.length > 0) {
      filters.ratings = this.activeFilters.rating;
    }
    if (this.activeFilters.ibu && this.activeFilters.ibu.length > 0) {
      filters.ibuIds = this.activeFilters.ibu;
    }
    if (this.activeFilters.workSite && this.activeFilters.workSite.length > 0) {
      filters.workSiteIds = this.activeFilters.workSite;
    }
    if (this.activeFilters.tenure && this.activeFilters.tenure.length > 0) {
      filters.tenureRanges = this.activeFilters.tenure;
    }
    if (this.activeFilters.experience && this.activeFilters.experience.length > 0) {
      filters.experienceRanges = this.activeFilters.experience;
    }
    if (this.activeFilters.responseType && this.activeFilters.responseType.length > 0) {
      if (this.activeFilters.responseType.includes('hasGood')) { filters.hasWhatWentGood = true; }
      if (this.activeFilters.responseType.includes('hasBad')) { filters.hasWhatWentBad = true; }
      if (this.activeFilters.responseType.includes('hasGoodOthers')) { filters.hasGoodOthers = true; }
      if (this.activeFilters.responseType.includes('hasBadOthers')) { filters.hasBadOthers = true; }
      if (this.activeFilters.responseType.includes('noFeedback')) {
        filters.hasWhatWentGood = false;
        filters.hasWhatWentBad = false;
        filters.hasGoodOthers = false;
        filters.hasBadOthers = false;
      }
    }
    if (this.activeFilters.anonymity && this.activeFilters.anonymity.length === 1) {
      filters.identityShared = this.activeFilters.anonymity[0] === 'named';
    }

    if (Object.keys(filters).length > 0) {
      req.filters = filters;
    }

    this.feedbackService.getMocDetailedResponses(req).subscribe((response: any) => {
      this.loading = false;
      if (response && response.statusCode === 200) {
        this.responsesData = response.responseData.responses;
        this.totalResponsesCount = response.responseData.totalCount;
        this.totalPages = response.responseData.totalPages;
      }
    }, error => {
      this.loading = false;
      console.error('Error fetching detailed responses:', error);
    });
  }

  onRatingFilterChange(rating: any) {
    this.selectedRating = rating;
    this.fetchDetailedResponses(1);
  }

  onMocFilter(filterObj: any) {
    if (this.skipNextFilterEmit) {
      this.skipNextFilterEmit = false;
      this.activeFilters = filterObj;
      this.lastFilterJson = JSON.stringify(filterObj);
      return;
    }
    const json = JSON.stringify(filterObj);
    if (json === this.lastFilterJson) return;
    this.lastFilterJson = json;
    this.activeFilters = filterObj;
    setTimeout(() => this.fetchDetailedResponses(1), 0);
  }

  get activeFilterChips(): { label: string; group: string }[] {
    const chips: { label: string; group: string }[] = [];
    for (const filter of this.mocFilters) {
      const selected = this.activeFilters[filter.type];
      if (selected && selected.length > 0) {
        for (const id of selected) {
          const item = filter.list.find((l: any) => l[filter.idField] === id || l[filter.idField] == id);
          chips.push({
            label: item ? item[filter.nameField] : String(id),
            group: filter.name
          });
        }
      }
    }
    return chips;
  }

  get appliedFilterCount(): number {
    return this.mocFilters.reduce((count, f) => {
      const selected = this.activeFilters[f.type];
      return count + (selected ? selected.length : 0);
    }, 0);
  }

  clearAllFilters() {
    this.mocFilters.forEach(filterData => {
      filterData.list.forEach(item => item.selected = false);
    });
    const stored = localStorage.getItem('filters');
    if (stored) {
      try {
        const localFilters = JSON.parse(this.utils.idDecryption(stored));
        delete localFilters[this.mocResource.type];
        localStorage.setItem('filters', this.utils.idEncryption(JSON.stringify(localFilters)));
      } catch (e) {}
    }
    this.activeFilters = {};
    this.lastFilterJson = '';
    this.mocFilterLoaded = false;
    setTimeout(() => {
      this.mocFilterLoaded = true;
      this.fetchDetailedResponses(1);
    }, 0);
  }

  onPageChange(page: number) {
    if (page >= 1 && page <= this.totalPages) {
      this.fetchDetailedResponses(page);
    }
  }

  onControlsDateRangeChange(range: Date[]) {
    if (range && range[0] && range[1]) {
      this.controlsStartDate = range[0];
      this.controlsEndDate = range[1];
    } else {
      this.controlsStartDate = null;
      this.controlsEndDate = null;
    }
  }

  downloadControlsReport() {
    if (!this.controlsStartDate || !this.controlsEndDate) { return; }
    this.controlsDownloading = true;
    const req = {
      formId: '1682358502664',
      startDate: moment(this.controlsStartDate).startOf('day').valueOf(),
      endDate: moment(this.controlsEndDate).endOf('day').valueOf()
    };
    this.feedbackService.downloadMocReport(req).subscribe((blob: Blob) => {
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `moc-report-${moment(this.controlsStartDate).format('YYYY-MM-DD')}-to-${moment(this.controlsEndDate).format('YYYY-MM-DD')}.csv`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
      this.controlsDownloading = false;
    }, () => { this.controlsDownloading = false; });
  }

  @HostListener('document:click', ['$event'])
  onDocumentClick(event: MouseEvent) {
    const target = event.target as HTMLElement;
    if (!document.contains(target)) { return; }
    if (!target.closest('.options-panel')) {
      this.addingGoodOption = false;
      this.addingBadOption = false;
    }
    if (!target.closest('.impact-card')) {
      this.newImpact = '';
      this.expandedImpactIds.clear();
    }
  }

  openGoodInput() {
    this.addingGoodOption = true;
    setTimeout(() => {
      const el = document.querySelector('.good-option-input') as HTMLInputElement;
      if (el) { el.focus(); }
    }, 0);
  }

  openBadInput() {
    this.addingBadOption = true;
    setTimeout(() => {
      const el = document.querySelector('.bad-option-input') as HTMLInputElement;
      if (el) { el.focus(); }
    }, 0);
  }

  saveGoodOption() {
    const label = this.newGoodOption.trim();
    if (!label || !this.goodQuestionId) { return; }
    const goodMaxOrder = this.goodOptions.length > 0 ? Math.max.apply(null, this.goodOptions.map(o => o.displayOrder)) + 1 : 1;
    this.feedbackService.addOption(this.goodQuestionId, label, goodMaxOrder).subscribe((response: any) => {
      if (response && response.response_data) {
        const o = response.response_data;
        this.goodOptions.push({ id: o.id, label: o.option_text, active: o.is_active, displayOrder: o.display_order, allowsOtherText: o.allows_other_text });
        this.goodOptions = this.sortOptions(this.goodOptions);
      }
      this.newGoodOption = '';
      this.addingGoodOption = false;
    }, () => {
      this.utils.growlMessages('error', 'Failed to add option. Please try again.');
    });
  }

  saveBadOption() {
    const label = this.newBadOption.trim();
    if (!label || !this.badQuestionId) { return; }
    const badMaxOrder = this.badOptions.length > 0 ? Math.max.apply(null, this.badOptions.map(o => o.displayOrder)) + 1 : 1;
    this.feedbackService.addOption(this.badQuestionId, label, badMaxOrder).subscribe((response: any) => {
      if (response && response.response_data) {
        const o = response.response_data;
        this.badOptions.push({ id: o.id, label: o.option_text, active: o.is_active, displayOrder: o.display_order, allowsOtherText: o.allows_other_text });
        this.badOptions = this.sortOptions(this.badOptions);
      }
      this.newBadOption = '';
      this.addingBadOption = false;
    }, () => {
      this.utils.growlMessages('error', 'Failed to add option. Please try again.');
    });
  }

  loadFeedbackOptions() {
    this.feedbackOptionsLoading = true;
    this.feedbackOptionsError = false;
    this.feedbackService.getDomains().subscribe((domainRes: any) => {
      const domains = domainRes && domainRes.response_data && domainRes.response_data.domains;
      const mocDomain = domains ? domains.find((d: any) => d.domain_key === 'mood_of_the_crew') : null;
      if (!mocDomain) {
        this.feedbackOptionsLoading = false;
        this.feedbackOptionsError = true;
        return;
      }
      this.feedbackService.getQuestions(mocDomain.domain_key).subscribe((qRes: any) => {
        const questions = qRes && qRes.response_data && qRes.response_data.questions;
        if (!questions) {
          this.feedbackOptionsLoading = false;
          this.feedbackOptionsError = true;
          return;
        }
        const multiSelectQuestions = questions
          .filter((q: any) => q.question_type === 'multi_select' && q.is_active)
          .sort((a: any, b: any) => a.display_order - b.display_order);
        if (multiSelectQuestions[0]) {
          this.goodQuestionId = multiSelectQuestions[0].id;
          this.goodPanelTitle = multiSelectQuestions[0].question_text;
          this.goodOptions = this.sortOptions(multiSelectQuestions[0].options
            .map((o: any) => ({ id: o.id, label: o.option_text, active: o.is_active, displayOrder: o.display_order, allowsOtherText: o.allows_other_text })));
        }
        if (multiSelectQuestions[1]) {
          this.badQuestionId = multiSelectQuestions[1].id;
          this.badPanelTitle = multiSelectQuestions[1].question_text;
          this.badOptions = this.sortOptions(multiSelectQuestions[1].options
            .map((o: any) => ({ id: o.id, label: o.option_text, active: o.is_active, displayOrder: o.display_order, allowsOtherText: o.allows_other_text })));
        }
        this.feedbackOptionsLoading = false;
      }, () => {
        this.feedbackOptionsLoading = false;
        this.feedbackOptionsError = true;
      });
    }, () => {
      this.feedbackOptionsLoading = false;
      this.feedbackOptionsError = true;
    });
  }

  deleteGoodOption(id: number) {
    this.confirmationService.confirm({
      message: 'Are you sure you want to permanently delete this option?',
      header: 'Delete Option',
      accept: () => {
        this.feedbackService.deleteOption(id).subscribe(() => {
          this.goodOptions = this.goodOptions.filter(o => o.id !== id);
        }, () => {
          this.utils.growlMessages('error', 'Failed to delete option. Please try again.');
        });
      }
    });
  }

  deleteBadOption(id: number) {
    this.confirmationService.confirm({
      message: 'Are you sure you want to permanently delete this option?',
      header: 'Delete Option',
      accept: () => {
        this.feedbackService.deleteOption(id).subscribe(() => {
          this.badOptions = this.badOptions.filter(o => o.id !== id);
        }, () => {
          this.utils.growlMessages('error', 'Failed to delete option. Please try again.');
        });
      }
    });
  }

  private sortOptions(options: any[]): any[] {
    return options.sort((a, b) => {
      if (a.allowsOtherText && !b.allowsOtherText) { return 1; }
      if (!a.allowsOtherText && b.allowsOtherText) { return -1; }
      return a.displayOrder - b.displayOrder;
    });
  }

  toggleGoodOption(opt: any) {
    const newActive = !opt.active;
    this.feedbackService.updateOption(opt.id, opt.label, opt.displayOrder, newActive, opt.allowsOtherText).subscribe(() => {
      opt.active = newActive;
    }, () => {
      this.utils.growlMessages('error', 'Failed to update option. Please try again.');
    });
  }

  toggleBadOption(opt: any) {
    const newActive = !opt.active;
    this.feedbackService.updateOption(opt.id, opt.label, opt.displayOrder, newActive, opt.allowsOtherText).subscribe(() => {
      opt.active = newActive;
    }, () => {
      this.utils.growlMessages('error', 'Failed to update option. Please try again.');
    });
  }

  private getCurrentWeekStartDate(): string {
    return this.getWeekBounds(moment()).start.format('YYYY-MM-DD');
  }

  loadWeekImpacts(weekStartDate: string) {
    this.impactsLoading = true;
    this.feedbackService.getMocImpacts(weekStartDate).subscribe((response: any) => {
      if (response && response.response_data && response.response_data.impact_updates) {
        this.weekImpacts = response.response_data.impact_updates.map((item: any) => ({
          id: item.week_start_date + '-' + item.sequence,
          text: item.impact_text,
          date: moment(item.created_at).format('MMM D'),
        }));
      } else {
        this.weekImpacts = [];
      }
      this.impactsLoading = false;
    }, () => {
      this.impactsLoading = false;
    });
  }

  saveImpact() {
    const text = this.newImpact.trim();
    if (!text || this.weekImpacts.length >= 2) { return; }
    const loggedInUser = this.utils.getLoggedInUser();
    const createdBy = loggedInUser ? loggedInUser.email : '';
    this.feedbackService.addMocImpact(text, createdBy).subscribe((response: any) => {
      if (response && response.response_data) {
        const item = response.response_data;
        this.weekImpacts.push({
          id: item.id,
          text: item.impact_text,
          date: moment(item.created_at).format('MMM D'),
        });
      }
      this.newImpact = '';
      this.addingImpact = false;
    }, () => {
      this.utils.growlMessages('error', 'Failed to save impact. Please try again.');
    });
  }

  removeImpact(id: string) {
    this.confirmationService.confirm({
      message: 'Are you sure you want to delete this impact?',
      header: 'Delete Confirmation',
      icon: 'fa fa-trash',
      accept: () => {
        this.feedbackService.deleteMocImpact(id).subscribe(() => {
          this.weekImpacts = this.weekImpacts.filter(i => i.id !== id);
          this.expandedImpactIds.delete(id);
        }, () => {
          this.utils.growlMessages('error', 'Failed to delete impact. Please try again.');
        });
      }
    });
  }

  downloadResponsesReport() {
    const req = {
      formId: '1682358502664',
      startDate: moment(this.daterange.start).startOf('day').valueOf(),
      endDate: moment(this.daterange.end).endOf('day').valueOf()
    };
    this.feedbackService.downloadMocReport(req).subscribe((blob: Blob) => {
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `moc-report-${moment(this.daterange.start).format('YYYY-MM-DD')}-to-${moment(this.daterange.end).format('YYYY-MM-DD')}.csv`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
    }, (error) => {
      console.error('Error downloading report:', error);
    });
  }

  get showingFrom(): number {
    return (this.currentPage - 1) * this.pageSize + 1;
  }

  get showingTo(): number {
    const to = this.currentPage * this.pageSize;
    return to > this.totalResponsesCount ? this.totalResponsesCount : to;
  }

  fetchProjectAnalytics() {
    const req = {
      formId: '1682358502664',
      startDate: moment(this.daterange.start).startOf('day').valueOf(),
      endDate: moment(this.daterange.end).endOf('day').valueOf()
    };

    this.feedbackService.getMocProjectAnalytics(req).subscribe((response: any) => {
      if (response && response.statusCode === 200) {
        this.topProjects = response.responseData.top10Projects || [];
        this.bottomProjects = response.responseData.bottom10Projects || [];
      }
    }, error => {
      console.error('Error fetching project analytics:', error);
    });
  }

  fetchDashboardStats() {
    const req = {
      formId: '1682358502664',
      startDate: moment(this.daterange.start).startOf('day').valueOf(),
      endDate: moment(this.daterange.end).endOf('day').valueOf()
    };

    this.feedbackService.getMocDashboardStats(req).subscribe((response: any) => {
      if (response && response.statusCode === 200) {
        this.dashboardStats = response.responseData;
      }
    }, error => {
      console.error('Error fetching dashboard stats:', error);
    });
  }

  fetchCategoryAnalytics() {
    this.loading = true;
    this.errorMessage = null;
    const req = {
      formId: '1682358502664',
      startDate: moment(this.daterange.start).startOf('day').valueOf(),
      endDate: moment(this.daterange.end).endOf('day').valueOf()
    };

    this.feedbackService.getMocCategoryAnalytics(req).subscribe((response: any) => {
      this.loading = false;
      if (response && response.statusCode === 200) {
        this.categoryAnalyticsData = response.responseData;
        this.prepareCategoryCharts();
        this.prepareSentimentData();
      }
    }, error => {
      this.loading = false;
      console.error('Error fetching category analytics:', error);
    });
  }

  private prepareCategoryCharts() {
    const data = this.categoryAnalyticsData;

    this.goodCategoryChart = Object.keys(data.goodFeedbackAnalytics).map(key => ({
      key: key,
      value: data.goodFeedbackAnalytics[key]
    }));

    this.badCategoryChart = Object.keys(data.badFeedbackAnalytics).map(key => ({
      key: key,
      value: data.badFeedbackAnalytics[key]
    }));
  }

  private prepareSentimentData() {
    const data = this.categoryAnalyticsData;
    const good = data.goodFeedbackAnalytics || {};
    const bad = data.badFeedbackAnalytics || {};

    // Merge case-insensitively so "Training Provided" and "Training provided" become one entry
    const mergedMap: { [norm: string]: { label: string; good: number; bad: number } } = {};

    Object.keys(good).forEach(key => {
      const norm = key.toLowerCase();
      if (!mergedMap[norm]) {
        mergedMap[norm] = { label: key, good: 0, bad: 0 };
      }
      mergedMap[norm].good += good[key] || 0;
    });

    Object.keys(bad).forEach(key => {
      const norm = key.toLowerCase();
      if (!mergedMap[norm]) {
        mergedMap[norm] = { label: key, good: 0, bad: 0 };
      }
      mergedMap[norm].bad += bad[key] || 0;
    });

    this.sentimentData = Object.keys(mergedMap).map(k => mergedMap[k]).map(item => {
      const total = item.good + item.bad;
      const goodPct = total > 0 ? Math.round((item.good / total) * 100) : 50;
      const badPct = total > 0 ? 100 - goodPct : 50;
      return {
        label: item.label,
        good: item.good,
        bad: item.bad,
        goodPct,
        badPct,
        netDiff: item.good - item.bad
      };
    }).sort((a, b) => b.netDiff - a.netDiff);
  }

  fetchCategoryTrend() {
    this.loading = true;
    this.errorMessage = null;
    const req = {
      formId: '1682358502664',
      weekCount: 10
    };

    this.feedbackService.getMocCategoryTrend(req).subscribe((response: any) => {
      this.loading = false;
      if (response && response.statusCode === 200) {
        this.trendData = response.responseData;
        this.prepareTrendCharts();
      }
    }, error => {
      this.loading = false;
      console.error('Error fetching category trends:', error);
    });
  }

  private prepareTrendCharts() {
    const trend = this.trendData;
    const weekLabels = trend.weekLabels.map(w => moment(w.weekStart).format('DD MMM'));

    this.goodTrendCharts = Object.keys(trend.goodFeedbackTrend).map(category => ({
      title: category,
      values: trend.goodFeedbackTrend[category],
      labels: weekLabels
    }));

    this.badTrendCharts = Object.keys(trend.badFeedbackTrend).map(category => ({
      title: category,
      values: trend.badFeedbackTrend[category],
      labels: weekLabels
    }));
  }

  get mostCreditedStrength(): string {
    if (!this.categoryAnalyticsData || !this.categoryAnalyticsData.goodFeedbackAnalytics) return '-';
    const good = this.categoryAnalyticsData.goodFeedbackAnalytics;
    const keys = Object.keys(good);
    if (!keys.length) return '-';
    return keys.reduce((a, b) => good[a] > good[b] ? a : b);
  }

  get mostFlaggedConcern(): string {
    if (!this.categoryAnalyticsData || !this.categoryAnalyticsData.badFeedbackAnalytics) return '-';
    const bad = this.categoryAnalyticsData.badFeedbackAnalytics;
    const keys = Object.keys(bad);
    if (!keys.length) return '-';
    return keys.reduce((a, b) => bad[a] > bad[b] ? a : b);
  }

  get mostFlaggedCount(): number {
    if (!this.categoryAnalyticsData || !this.categoryAnalyticsData.badFeedbackAnalytics || this.mostFlaggedConcern === '-') return 0;
    return this.categoryAnalyticsData.badFeedbackAnalytics[this.mostFlaggedConcern] || 0;
  }

  get lowestRatedProject(): string {
    if (!this.bottomProjects || !this.bottomProjects.length) return null;
    const p = this.bottomProjects[0];
    return p.projectName || p.name || p.project || null;
  }

  getTrendOptions() {
    return {
      responsive: true,
      maintainAspectRatio: false,
      legend: { display: false },
      scales: {
        yAxes: [{
          ticks: { beginAtZero: true, stepSize: 1 }
        }]
      }
    };
  }
}